import React, { useMemo, useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./NewGlobalDataTable.css";

const NewDataTable = ({
    columns,
    data,
    tableName,
    totalRecord,
}) => {
    data = Array.isArray(data) ? data : [];

    const [searchText, setSearchText] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [columnWidths, setColumnWidths] = useState({});
    const [isMobile, setIsMobile] = useState(false);

    // Detect screen size
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);


    const handleSearch = (event) => {
        setSearchText(event.target.value);
        setPage(1);
    };


    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setPage(1);
    };

    const handlePageChange = (direction) => {
        setPage((prev) => Math.max(1, prev + direction));
    };

    const handleSort = (columnIndex) => {
        const key = columns[columnIndex].name;
        const direction =
            sortConfig.key === key && sortConfig.direction === "asc"
                ? "desc"
                : "asc";
        setSortConfig({ key, direction });
    };

    // Resize
    const startResize = (e, index) => {
        e.preventDefault();
        const startX = e.clientX;
        const th = e.target.parentElement;
        const startWidth = th.offsetWidth;

        const handleMouseMove = (moveEvent) => {
            const newWidth = Math.max(60, startWidth + moveEvent.clientX - startX);
            setColumnWidths((prev) => ({ ...prev, [index]: `${newWidth}px` }));
        };

        const handleMouseUp = () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    // Filter + Sort
    const filteredData = useMemo(() => {
        let tempData = [...data];

        if (searchText) {
            tempData = tempData.filter((item) =>
                columns.some((col) => {
                    const val = col.selector?.(item);
                    return (
                        val &&
                        val.toString().toLowerCase().includes(searchText.toLowerCase())
                    );
                })
            );
        }

        if (sortConfig.key) {
            const col = columns.find((c) => c.name === sortConfig.key);
            if (col && col.sortable) {
                tempData.sort((a, b) => {
                    const va = col.selector?.(a);
                    const vb = col.selector?.(b);
                    if (va < vb) return sortConfig.direction === "asc" ? -1 : 1;
                    if (va > vb) return sortConfig.direction === "asc" ? 1 : -1;
                    return 0;
                });
            }
        }

        return tempData;
    }, [data, searchText, sortConfig, columns]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = filteredData.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );


    return (
        <div className="datatable-container p-3 border rounded">

            {/* Top Controls */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <h5 className="fw-bold text-dark">{tableName}</h5>

                <input
                    type="text"
                    value={searchText}
                    onChange={handleSearch}
                    placeholder="Search"
                    className="form-control form-control-sm w-25 cs-search"
                />
            </div>

            {/* ----------------------------------- */}
            {/* DESKTOP TABLE VIEW (>=768px)        */}
            {/* ----------------------------------- */}
            {!isMobile && (
                <table className="table table-bordered table-hover table-striped table-sm align-middle modern-table">
                    <thead className="table-primary text-center">
                        <tr>
                            {columns.map((col, index) => (
                                <th
                                    key={index}
                                    onClick={() => col.sortable && handleSort(index)}
                                    style={{
                                        cursor: col.sortable ? "pointer" : "default",
                                        width: columnWidths[index] || "auto",
                                        position: "relative",
                                        textAlign: "center",
                                    }}
                                >
                                    {col.name}
                                    {sortConfig.key === col.name && (
                                        <small className="ms-1">
                                            {sortConfig.direction === "asc" ? "▲" : "▼"}
                                        </small>
                                    )}
                                    <div
                                        className="resize-handle"
                                        onMouseDown={(e) => startResize(e, index)}
                                    />
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col, colIndex) => (
                                        <td key={colIndex}
                                            style={{ textAlign: col.align ?? "left" }} >
                                            {col.cell
                                                ? col.cell(row, rowIndex)
                                                : col.selector?.(row, rowIndex)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center text-muted">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {/* ----------------------------------- */}
            {/* MOBILE CARD VIEW (<768px)          */}
            {/* ----------------------------------- */}
            {isMobile && (
                <div className="mobile-card-wrapper">
                    {paginatedData.map((row, idx) => (
                        <div key={idx} className="mobile-card shadow-sm p-3 mb-3 rounded-3">
                            {columns.map((col, i) => (
                                <div key={i} className="d-flex mb-2">
                                    <strong className="me-2 text-primary">
                                        {col.name}:
                                    </strong>
                                    <span className="text-dark">
                                        {col.cell
                                            ? col.cell(row, idx)
                                            : col.selector?.(row, idx)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {/* Bottom Controls */}
            <div className="d-flex justify-content-between align-items-center flex-wrap mt-2">
                <div className="d-flex align-items-center">
                    <label className="me-2 fw-semibold text-secondary">
                        Page Size:
                    </label>
                    <select
                        value={rowsPerPage}
                        className="form-select form-select-sm w-auto"
                        onChange={handleRowsPerPageChange}
                    >
                        {[10, 20, 30].map((num) => (
                            <option key={num} value={num}>
                                {num}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    Total: {totalRecord} &nbsp;|&nbsp; Page {page}/{totalPages || 1}
                    &nbsp;&nbsp;
                    <button
                        className="btn btn-secondary btn-sm me-2 cs-bottom"
                        onClick={() => handlePageChange(-1)}
                        disabled={page === 1}
                    >
                        Prev
                    </button>
                    <button
                        className="btn btn-secondary btn-sm cs-bottom"
                        onClick={() => handlePageChange(1)}
                        disabled={page === totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewDataTable;
